import React, { useState, useEffect, useMemo } from 'react';
import api from './api';
import { RomAssessmentPanel } from './RomAssessment';
import {
  ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, BarChart, CartesianGrid, Cell,
} from 'recharts';

/* ── Body part key mapping: consultation key → ROM assessment key ── */
export const CONSULT_TO_ROM_PART = {
  // Neck / Spine
  cervical:    'cervical',  neckback:    'cervical',
  upperback:   'thoracic',  middleback:  'thoracic',
  lowerback:   'lowerBack', coccyx:      'lowerBack',
  // Shoulder
  shoulder_L:  'shoulder',  shoulder_R:  'shoulder',
  shoulder_LB: 'shoulder',  shoulder_RB: 'shoulder',
  // Elbow / Arm
  arm_L:       'elbow',     arm_R:       'elbow',
  arm_LB:      'elbow',     arm_RB:      'elbow',
  elbow_L:     'elbow',     elbow_R:     'elbow',
  elbow_LB:    'elbow',     elbow_RB:    'elbow',
  // Wrist / Hand
  wrist_L:     'wrist',     wrist_R:     'wrist',
  palm_L:      'wrist',     palm_R:      'wrist',
  fingers_L:   'wrist',     fingers_R:   'wrist',
  // Hip
  hip_L:       'hip',       hip_R:       'hip',
  hip_LB:      'hip',       hip_RB:      'hip',
  thigh_L:     'hip',       thigh_R:     'hip',
  reproductive:'hip',
  // Knee
  knee_L:      'knee',      knee_R:      'knee',
  knee_LB:     'knee',      knee_RB:     'knee',
  leg_L:       'knee',      leg_R:       'knee',
  hamstring_L: 'knee',      hamstring_R: 'knee',
  // Ankle / Foot
  calf_L:      'ankle',     calf_R:      'ankle',
  calf_LB:     'ankle',     calf_RB:     'ankle',
  ankle_L:     'ankle',     ankle_R:     'ankle',
  ankle_LB:    'ankle',     ankle_RB:    'ankle',
  foot_L:      'ankle',     foot_R:      'ankle',
  toes_L:      'ankle',     toes_R:      'ankle',
  heel_L:      'ankle',     heel_R:      'ankle',
};

export const ROM_PART_LABELS = {
  cervical:   'Neck (Cervical)',
  thoracic:   'Thoracic Spine',
  shoulder:   'Shoulder',
  elbow:      'Elbow',
  wrist:      'Wrist',
  upperBack:  'Upper Back',
  middleBack: 'Middle Back',
  lowerBack:  'Lower Back',
  hip:        'Hip',
  knee:       'Knee',
  ankle:      'Ankle',
};

export const CHART_COLORS = [
  '#2196f3', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#f97316', '#06b6d4', '#84cc16', '#ec4899', '#6366f1',
  '#0ea5e9', '#dc2626', '#059669', '#d97706', '#7c3aed',
];

const SYMPTOM_COLORS = {
  pain: '#ef4444', numbness: '#f59e0b', weakness: '#8b5cf6',
  stiffness: '#3b82f6', burning: '#f97316', spasm: '#10b981', instability: '#6b7280',
  recommendationScore: '#16a34a', overallImprovement: '#0891b2',
};
const SYMPTOM_LIST = ['pain', 'numbness', 'weakness', 'stiffness', 'burning', 'spasm', 'instability'];

/* ── Format a ROM/Muscle key for display ── */
function formatKeyLabel(k) {
  if (k.startsWith('ms_')) {
    const rest = k.slice(3).replace(/_L$/, ' L').replace(/_R$/, ' R');
    return rest.charAt(0).toUpperCase() + rest.slice(1) + ' (MVC)';
  }
  return k
    .replace(/_L$/, ' L').replace(/_R$/, ' R')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, s => s.toUpperCase());
}

/* ── Extract per-part time-series data from history snapshots ── */
export function buildPartHistory(history, romPartKey) {
  const romKeys = new Set();
  const msKeys  = new Set();

  const rows = (history || []).map(snap => {
    const partRom = snap.rom?.[romPartKey] || {};
    const partMs  = snap.muscleStrength?.[romPartKey] || {};
    const dateObj = new Date(snap.assessmentDate);
    const label   = isNaN(dateObj)
      ? '—'
      : dateObj.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
    const row = { date: label, _rawDate: snap.assessmentDate };

    Object.entries(partRom).forEach(([moveKey, val]) => {
      if (!val || typeof val !== 'object') return;
      if ('left' in val || 'right' in val) {
        if (val.left?.active  != null) { row[`${moveKey}_L`] = Number(val.left.active);  romKeys.add(`${moveKey}_L`); }
        if (val.right?.active != null) { row[`${moveKey}_R`] = Number(val.right.active); romKeys.add(`${moveKey}_R`); }
      } else if (val.active != null) {
        row[moveKey] = Number(val.active); romKeys.add(moveKey);
      }
    });

    Object.entries(partMs).forEach(([grpKey, val]) => {
      if (!val || typeof val !== 'object') return;
      if ('left' in val || 'right' in val) {
        if (val.left?.grade  != null) { row[`ms_${grpKey}_L`] = Number(val.left.grade);  msKeys.add(`ms_${grpKey}_L`); }
        if (val.right?.grade != null) { row[`ms_${grpKey}_R`] = Number(val.right.grade); msKeys.add(`ms_${grpKey}_R`); }
      } else if (val.grade != null) {
        row[`ms_${grpKey}`] = Number(val.grade); msKeys.add(`ms_${grpKey}`);
      }
    });

    return row;
  });

  return { rows, romKeys: [...romKeys], msKeys: [...msKeys] };
}

/* ── Custom chart tooltip (dark) ── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1e293b', border: '1px solid rgba(255,255,255,.12)',
      borderRadius: 8, padding: '8px 12px', fontSize: 11,
      boxShadow: '0 6px 20px rgba(0,0,0,.35)', maxWidth: 220,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#e2e8f0', fontSize: 12 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 12, height: 3, background: p.color, display: 'inline-block', borderRadius: 2 }} />
          <span style={{ color: '#94a3b8' }}>{p.name}:</span>
          <strong style={{ color: p.color }}>
            {String(p.dataKey).startsWith('ms_') ? `MVC ${p.value}/5` : `${p.value}°`}
          </strong>
        </div>
      ))}
    </div>
  );
}

/* ── Custom pain trend tooltip ── */
function PainTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#1e293b', border: '1px solid rgba(255,255,255,.12)',
      borderRadius: 8, padding: '8px 12px', fontSize: 11,
      boxShadow: '0 6px 20px rgba(0,0,0,.35)', maxWidth: 200,
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#e2e8f0', fontSize: 12 }}>{label}</div>
      {payload.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color, display: 'inline-block' }} />
          <span style={{ color: '#94a3b8' }}>{p.name}:</span>
          <strong style={{ color: p.color }}>{p.value}/10</strong>
        </div>
      ))}
    </div>
  );
}

/* ── Toggle button helper ── */
function ViewToggle({ mode, onChange }) {
  return (
    <button
      onClick={() => onChange(mode === 'chart' ? 'table' : 'chart')}
      style={{
        fontSize: 11, padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db',
        background: '#f9fafb', color: '#374151', cursor: 'pointer', fontWeight: 600,
        display: 'flex', alignItems: 'center', gap: 4,
      }}
    >
      {mode === 'chart' ? '📋 Table' : '📈 Chart'}
    </button>
  );
}

/* ── Table wrapper ── */
const TH = ({ children, color }) => (
  <th style={{
    padding: '7px 10px', textAlign: 'center', fontWeight: 600, fontSize: 11,
    color: color || '#374151', borderBottom: '2px solid #e5e7eb',
    background: '#f8fafc', whiteSpace: 'nowrap',
  }}>{children}</th>
);
const TD = ({ children }) => (
  <td style={{ padding: '6px 10px', textAlign: 'center', fontSize: 12, borderBottom: '1px solid #f0f0f0' }}>
    {children}
  </td>
);

/* ══════════════════════════════════════════════════════════════
   PartProgressChart — one card per body part, time-series
   mode: 'all' | 'rom' | 'muscle'
   Named export so PatientDashboard can reuse.
══════════════════════════════════════════════════════════════ */
export function PartProgressChart({ romPartKey, label, history, mode = 'all', onDeleteRow, onDeletePart }) {
  const [viewMode, setViewMode] = useState('chart');

  const { rows, romKeys, msKeys } = useMemo(
    () => buildPartHistory(history, romPartKey),
    [history, romPartKey]
  );

  const activeRomKeys = mode === 'muscle' ? [] : romKeys;
  const activeMsKeys  = mode === 'rom'    ? [] : msKeys;
  const hasData   = activeRomKeys.length > 0 || activeMsKeys.length > 0;
  const hasMuscle = activeMsKeys.length > 0 && mode !== 'rom';
  const hasRom    = activeRomKeys.length > 0;
  const chartW    = Math.max(520, Math.min(780, rows.length * 90 + 120));

  /* Y-axis config */
  const mainDomain = (mode === 'muscle' && !hasRom) ? [0, 5] : [0, 'auto'];
  const mainLabel  = (mode === 'muscle' && !hasRom) ? 'MVC' : '°';

  const legendNote = mode === 'all'
    ? 'Solid = ROM (°)  ·  Dashed = Muscle MVC (0–5)'
    : mode === 'rom'
      ? 'ROM measurements in degrees (°)'
      : 'Muscle strength – MVC grade (0–5)';

  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '14px 18px',
      boxShadow: '0 2px 10px rgba(0,0,0,.08)', marginBottom: 18,
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
          📌 {label}
          {rows.length === 1 && hasData && (
            <span style={{ fontSize: 10, fontWeight: 400, color: '#f59e0b', marginLeft: 10 }}>
              ⚠ 1 snapshot — save more assessments to see trends
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {hasData && <ViewToggle mode={viewMode} onChange={setViewMode} />}
          {onDeletePart && (
            <button onClick={onDeletePart} title="Delete all readings for this body part"
              style={{ fontSize: 13, padding: '3px 8px', borderRadius: 6, border: '1px solid #fca5a5',
                background: '#fff5f5', color: '#dc2626', cursor: 'pointer', fontWeight: 600 }}>
              🗑
            </button>
          )}
        </div>
      </div>

      {!hasData ? (
        <div style={{ color: '#aaa', fontSize: 12, padding: '10px 0' }}>
          No {mode === 'muscle' ? 'Muscle' : mode === 'rom' ? 'ROM' : 'ROM / Muscle'} data for this body part yet.
        </div>
      ) : viewMode === 'chart' ? (
        <div style={{ overflowX: 'auto' }}>
          <ComposedChart
            width={chartW} height={220} data={rows}
            margin={{ top: 6, right: hasMuscle ? 54 : 20, left: 0, bottom: 6 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis
              yAxisId="main" domain={mainDomain} tick={{ fontSize: 10 }}
              label={{ value: mainLabel, angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#555' } }}
            />
            {hasMuscle && (
              <YAxis
                yAxisId="muscle" orientation="right"
                domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} tick={{ fontSize: 10 }}
                label={{ value: 'MVC', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#555' } }}
              />
            )}
            <Tooltip content={<ChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            {activeRomKeys.map((k, i) => (
              <Line key={k} yAxisId="main" type="monotone" dataKey={k}
                stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2}
                dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls name={formatKeyLabel(k)} />
            ))}
            {activeMsKeys.map((k, i) => (
              <Line key={k} yAxisId={hasMuscle ? 'muscle' : 'main'} type="monotone" dataKey={k}
                stroke={CHART_COLORS[(activeRomKeys.length + i) % CHART_COLORS.length]}
                strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} activeDot={{ r: 5 }}
                connectNulls name={formatKeyLabel(k)} />
            ))}
          </ComposedChart>
          <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 2 }}>{legendNote}</div>
        </div>
      ) : (
        /* TABLE VIEW */
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <TH>Date</TH>
                {activeRomKeys.map(k => <TH key={k} color="#1565c0">{formatKeyLabel(k)}</TH>)}
                {activeMsKeys.map(k => <TH key={k} color="#7b1fa2">{formatKeyLabel(k)}</TH>)}
                {onDeleteRow && <TH></TH>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: i % 2 ? '#fafbfd' : '#fff' }}>
                  <TD><strong>{r.date}</strong></TD>
                  {activeRomKeys.map(k => (
                    <TD key={k}>
                      {r[k] != null
                        ? <span style={{ fontWeight: 700, color: '#2196f3' }}>{r[k]}°</span>
                        : <span style={{ color: '#d1d5db' }}>—</span>}
                    </TD>
                  ))}
                  {activeMsKeys.map(k => (
                    <TD key={k}>
                      {r[k] != null
                        ? <span style={{ fontWeight: 700, color: '#7b1fa2' }}>MVC {r[k]}/5</span>
                        : <span style={{ color: '#d1d5db' }}>—</span>}
                    </TD>
                  ))}
                  {onDeleteRow && (
                    <TD>
                      <button
                        onClick={() => onDeleteRow(r._rawDate)}
                        title="Delete this reading"
                        style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4,
                          border: '1px solid #fca5a5', background: '#fff5f5',
                          color: '#dc2626', cursor: 'pointer' }}
                      >🗑</button>
                    </TD>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   AllRomPartsProgress — all body parts from history
   Named export for PatientDashboard.
══════════════════════════════════════════════════════════════ */
export function AllRomPartsProgress({ romHistory, loading, mode = 'all' }) {
  const allParts = useMemo(() => {
    const map = new Map();
    (romHistory || []).forEach(snap => {
      Object.keys(snap.rom || {}).forEach(k => { if (!map.has(k)) map.set(k, ROM_PART_LABELS[k] || k); });
      Object.keys(snap.muscleStrength || {}).forEach(k => { if (!map.has(k)) map.set(k, ROM_PART_LABELS[k] || k); });
    });
    return [...map.entries()].map(([key, label]) => ({ key, label }));
  }, [romHistory]);

  if (loading) {
    return <div style={{ color: '#aaa', padding: '40px 0', textAlign: 'center', fontSize: 13 }}>Loading assessment history…</div>;
  }
  if (!romHistory?.length) {
    return (
      <div style={{ color: '#9ca3af', padding: '32px 0', textAlign: 'center', fontSize: 13 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>📐</div>
        No ROM assessment history yet.<br />
        <span style={{ fontSize: 11 }}>Save an assessment to start tracking progress.</span>
      </div>
    );
  }
  if (!allParts.length) {
    return <div style={{ color: '#aaa', padding: '32px 0', textAlign: 'center', fontSize: 13 }}>No body part data in assessment history.</div>;
  }

  return (
    <div>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 14 }}>
        {romHistory.length} snapshot{romHistory.length !== 1 ? 's' : ''} recorded
        {romHistory.length >= 2 && ` · from ${new Date(romHistory[0].assessmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} to ${new Date(romHistory[romHistory.length - 1].assessmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`}
      </div>
      {allParts.map(({ key, label }) => (
        <PartProgressChart key={key} romPartKey={key} label={label} history={romHistory} mode={mode} />
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   PatientProgressModal — default export, main modal
════════════════════════════════════════════════════════ */
export default function PatientProgressModal({
  patientCard, patientName, sessions, selectedRecomms, bodySymptoms, onClose,
}) {
  const [tab, setTab]               = useState('overview');
  const [romHistory, setRomHistory] = useState([]);
  const [reviewHistory, setReviewHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [showRomPanel, setShowRomPanel] = useState(false);
  const [painViewMode, setPainViewMode] = useState('chart');
  const [bpRomKeyMap, setBpRomKeyMap] = useState(CONSULT_TO_ROM_PART); // fallback to static
  const [romPartLabels, setRomPartLabels] = useState(ROM_PART_LABELS); // fallback to static
  const [visibleSymptoms, setVisibleSymptoms] = useState(null); // null = all visible initially

  useEffect(() => {
    if (!patientCard) return;
    setHistLoading(true);
    Promise.all([
      api.get(`${process.env.REACT_APP_URL}/patients/rom/${patientCard}/history`).catch(() => ({ data: [] })),
      api.get(`${process.env.REACT_APP_URL}/patients/consultation/${patientCard}/review-history`).catch(() => ({ data: [] })),
      api.get(`${process.env.REACT_APP_URL}/bodyparts`).catch(() => ({ data: null })),
      api.get(`${process.env.REACT_APP_URL}/bodyparts/clinical`).catch(() => ({ data: null })),
    ]).then(([romRes, revRes, bpRes, clinRes]) => {
      setRomHistory(Array.isArray(romRes.data) ? romRes.data : []);
      setReviewHistory(Array.isArray(revRes.data) ? revRes.data : []);
      // Build romKey map from BPConfig annotations
      if (bpRes?.data) {
        const allBp = [...(bpRes.data.front || []), ...(bpRes.data.back || [])];
        const map = {};
        allBp.forEach(a => { if (a.romKey) map[a.key] = a.romKey; });
        if (Object.keys(map).length > 0) setBpRomKeyMap(map);
      }
      // Build ROM part labels from ClinicalConfig
      if (clinRes?.data?.romParts?.length > 0) {
        const labels = {};
        clinRes.data.romParts.forEach(rp => { labels[rp.key] = rp.label; });
        setRomPartLabels(labels);
      }
    }).finally(() => setHistLoading(false));
  }, [patientCard]);

  /* ── Consultation body parts → ROM keys (from BPConfig annotation romKey) ── */
  const consultRomParts = useMemo(() => {
    const parts = new Map();
    Object.keys(bodySymptoms || {}).forEach(ck => {
      const rk = bpRomKeyMap[ck];
      if (rk && !parts.has(rk)) parts.set(rk, romPartLabels[rk] || rk);
    });
    return parts;
  }, [bodySymptoms, bpRomKeyMap, romPartLabels]);

  /* ── Pain + recomm trend per session ── */
  const completedSessions = (sessions || []).filter(s => s.status === 'Completed' && s.sessionFeedback);
  const painTrendData = completedSessions.map((s, i) => {
    const row = {
      label: `S${i + 1}`,
      fullDate: s.start
        ? new Date(s.start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
        : '—',
    };
    SYMPTOM_LIST.forEach(k => { if (s.sessionFeedback[k] != null) row[k] = s.sessionFeedback[k]; });
    if (s.sessionFeedback?.recommendationScore != null) row.recommendationScore = s.sessionFeedback.recommendationScore;
    if (s.sessionFeedback?.overallImprovement  != null) row.overallImprovement  = s.sessionFeedback.overallImprovement;
    return row;
  });

  const activeSymptoms = SYMPTOM_LIST.filter(sym => painTrendData.some(d => d[sym] != null));
  const hasRecommScore = painTrendData.some(d => d.recommendationScore != null);
  const hasImprovement  = painTrendData.some(d => d.overallImprovement  != null);

  // Symptom filter: all keys that can be toggled (symptoms + recomm + improvement)
  const allToggleKeys = useMemo(() => {
    const keys = [...activeSymptoms];
    if (hasRecommScore) keys.push('recommendationScore');
    if (hasImprovement) keys.push('overallImprovement');
    return keys;
  }, [activeSymptoms, hasRecommScore, hasImprovement]);

  // Initialize visibleSymptoms with all active on first render
  useEffect(() => {
    if (visibleSymptoms === null && allToggleKeys.length > 0) {
      setVisibleSymptoms(new Set(allToggleKeys));
    }
  }, [allToggleKeys, visibleSymptoms]);

  const toggleSymptom = (key) => {
    setVisibleSymptoms(prev => {
      const next = new Set(prev || allToggleKeys);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleAllSymptoms = () => {
    const vs = visibleSymptoms || new Set(allToggleKeys);
    setVisibleSymptoms(vs.size === allToggleKeys.length ? new Set() : new Set(allToggleKeys));
  };
  const shownSymptoms = activeSymptoms.filter(s => !visibleSymptoms || visibleSymptoms.has(s));
  const showRecommLine = hasRecommScore && (!visibleSymptoms || visibleSymptoms.has('recommendationScore'));
  const showImproveLine = hasImprovement && (!visibleSymptoms || visibleSymptoms.has('overallImprovement'));

  /* ── All ROM parts from history (not just consultation parts) ── */
  const allRomParts = useMemo(() => {
    const parts = new Map();
    // First add consultation body parts
    Object.keys(bodySymptoms || {}).forEach(ck => {
      const rk = bpRomKeyMap[ck];
      if (rk && !parts.has(rk)) parts.set(rk, { label: romPartLabels[rk] || rk, isConsult: true });
    });
    // Then add ALL parts from ROM history
    (romHistory || []).forEach(snap => {
      Object.keys(snap.rom || {}).forEach(k => {
        if (!parts.has(k)) parts.set(k, { label: romPartLabels[k] || ROM_PART_LABELS[k] || k, isConsult: false });
      });
      Object.keys(snap.muscleStrength || {}).forEach(k => {
        if (!parts.has(k)) parts.set(k, { label: romPartLabels[k] || ROM_PART_LABELS[k] || k, isConsult: false });
      });
    });
    return parts;
  }, [bodySymptoms, bpRomKeyMap, romPartLabels, romHistory]);

  /* ── Recommendation compliance trend data ── */
  const recommTrendData = useMemo(() => {
    return completedSessions.map((s, i) => {
      const fb = s.sessionFeedback || {};
      return {
        label: 'S' + (i + 1),
        fullDate: s.start ? new Date(s.start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—',
        recommendationScore: fb.recommendationScore != null ? fb.recommendationScore : null,
        overallImprovement: fb.overallImprovement != null ? fb.overallImprovement : null,
      };
    }).filter(d => d.recommendationScore != null || d.overallImprovement != null);
  }, [completedSessions]);

  const TABS = [
    { key: 'overview',  label: '🏠 Overview'       },
    { key: 'pain',      label: '📊 Pain & Recomm.'  },
    { key: 'progress',  label: '📈 ROM & Muscle'    },
    { key: 'recomm',    label: '📋 Recomm. List'    },
  ];

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      paddingTop: 32, paddingBottom: 32, overflowY: 'auto',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#f8fafc', borderRadius: 18,
        width: '92vw', maxWidth: 940,
        boxShadow: '0 16px 60px rgba(0,0,0,0.28)',
        display: 'flex', flexDirection: 'column',
        maxHeight: '90vh', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '16px 24px',
          background: 'linear-gradient(90deg,#1e3a5f,#1565c0)',
          borderRadius: '18px 18px 0 0', flexShrink: 0,
        }}>
          <span style={{ fontSize: 20, flex: 1, fontWeight: 700, color: '#fff' }}>
            📈 Progress — {patientName || patientCard}
          </span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,.55)', marginRight: 8 }}>
            {completedSessions.length} sessions · {romHistory.length} ROM snapshots
          </span>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#f87171',
            fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: '2px 6px',
          }}>✕</button>
        </div>

        {/* Tab bar */}
        <div style={{
          display: 'flex', background: '#fff',
          borderBottom: '1px solid #e5e7eb', flexShrink: 0, overflowX: 'auto',
        }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding: '11px 20px', border: 'none', cursor: 'pointer',
              background: 'none', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
              color: tab === t.key ? '#1565c0' : '#6b7280',
              borderBottom: tab === t.key ? '3px solid #1565c0' : '3px solid transparent',
              transition: 'all 0.15s',
            }}>{t.label}</button>
          ))}
        </div>

        {/* Body — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ══ OVERVIEW ══ */}
          {tab === 'overview' && (
            <div>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
                {[
                  { icon: '🗓️', label: 'Total Sessions',    value: sessions?.length || 0,         color: '#1976d2' },
                  { icon: '✅', label: 'Completed',          value: completedSessions.length,       color: '#2e7d32' },
                  { icon: '📐', label: 'ROM Snapshots',      value: histLoading ? '…' : romHistory.length, color: '#7b1fa2' },
                  { icon: '📌', label: 'Parts Tracked',      value: histLoading ? '…' : consultRomParts.size || '—', color: '#e65100' },
                  { icon: '📅', label: 'Last ROM',           value: romHistory.length
                      ? new Date(romHistory[romHistory.length - 1].assessmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
                      : 'Not done',
                    color: '#00897b' },
                ].map(c => (
                  <div key={c.label} style={{
                    flex: '1 1 130px', minWidth: 120, background: '#fff', borderRadius: 12,
                    padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,.07)',
                    borderLeft: `4px solid ${c.color}`,
                  }}>
                    <div style={{ fontSize: 20, marginBottom: 2 }}>{c.icon}</div>
                    <div style={{ fontSize: 22, fontWeight: 800, color: c.color, lineHeight: 1.1 }}>{c.value}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginTop: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{c.label}</div>
                  </div>
                ))}
              </div>

              {completedSessions.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,.07)' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1d23', marginBottom: 12 }}>📋 Recent Session Feedback</div>
                  {completedSessions.slice(-5).reverse().map((s, i) => (
                    <div key={s._id || i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: 10, borderBottom: i < 4 ? '1px solid #f0f0f0' : 'none', marginBottom: i < 4 ? 10 : 0 }}>
                      <span style={{ fontSize: 11, color: '#888', minWidth: 90 }}>
                        {s.start ? new Date(s.start).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                      </span>
                      <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', flex: 1 }}>
                        {SYMPTOM_LIST.map(sym => (s.sessionFeedback?.[sym] || 0) > 0 ? (
                          <span key={sym} style={{
                            fontSize: 10, background: '#f5f5f5', borderRadius: 4, padding: '1px 6px',
                            borderLeft: `3px solid ${SYMPTOM_COLORS[sym]}`,
                          }}>
                            {sym.slice(0, 3)}: <strong style={{ color: SYMPTOM_COLORS[sym] }}>{s.sessionFeedback[sym]}</strong>
                          </span>
                        ) : null)}
                        {(s.sessionFeedback?.recommendationScore != null) && (
                          <span style={{ fontSize: 10, background: '#f0fdf4', borderRadius: 4, padding: '1px 6px', borderLeft: '3px solid #16a34a' }}>
                            recomm: <strong style={{ color: '#16a34a' }}>{s.sessionFeedback.recommendationScore}/10</strong>
                          </span>
                        )}
                        {(s.sessionFeedback?.overallImprovement != null) && (
                          <span style={{ fontSize: 10, background: '#f0fdfa', borderRadius: 4, padding: '1px 6px', borderLeft: '3px solid #0891b2' }}>
                            improvement: <strong style={{ color: '#0891b2' }}>{s.sessionFeedback.overallImprovement}/10</strong>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {completedSessions.length === 0 && (
                <div style={{ color: '#aaa', textAlign: 'center', padding: '30px 0', fontSize: 13 }}>No completed sessions with feedback yet.</div>
              )}
            </div>
          )}

          {/* ══ PAIN & RECOMM TREND ══ */}
          {tab === 'pain' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1d23' }}>
                  📊 Symptom &amp; Recommendation Trend
                </div>
                {painTrendData.length >= 2 && <ViewToggle mode={painViewMode} onChange={setPainViewMode} />}
              </div>

              {/* ── Symptom filter chips ── */}
              {allToggleKeys.length > 0 && painTrendData.length >= 2 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                  <button onClick={toggleAllSymptoms} style={{
                    fontSize: 10, padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontWeight: 700,
                    border: '1px solid #d1d5db',
                    background: (visibleSymptoms || new Set()).size === allToggleKeys.length ? '#1e3a5f' : '#f3f4f6',
                    color: (visibleSymptoms || new Set()).size === allToggleKeys.length ? '#fff' : '#374151',
                  }}>All</button>
                  {allToggleKeys.map(key => {
                    const on = !visibleSymptoms || visibleSymptoms.has(key);
                    const color = SYMPTOM_COLORS[key] || '#6b7280';
                    const label = key === 'recommendationScore' ? 'Recomm. Score'
                      : key === 'overallImprovement' ? 'Overall Impr.'
                      : key.charAt(0).toUpperCase() + key.slice(1);
                    return (
                      <button key={key} onClick={() => toggleSymptom(key)} style={{
                        fontSize: 10, padding: '4px 10px', borderRadius: 20, cursor: 'pointer', fontWeight: 600,
                        border: `1.5px solid ${on ? color : '#e5e7eb'}`,
                        background: on ? color + '18' : '#f9fafb',
                        color: on ? color : '#aaa',
                        transition: 'all .15s',
                      }}>{on ? '● ' : '○ '}{label}</button>
                    );
                  })}
                </div>
              )}

              {painTrendData.length < 2 ? (
                <div style={{ color: '#aaa', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>
                  Need at least 2 completed sessions with feedback to show trends.
                </div>
              ) : painViewMode === 'chart' ? (
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 12px', boxShadow: '0 2px 8px rgba(0,0,0,.07)', overflowX: 'auto' }}>
                  <LineChart
                    width={Math.min(840, painTrendData.length * 68 + 100)} height={300}
                    data={painTrendData}
                    margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11 }} />
                    <Tooltip content={<PainTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {shownSymptoms.map(sym => (
                      <Line key={sym} type="monotone" dataKey={sym}
                        stroke={SYMPTOM_COLORS[sym]} strokeWidth={2}
                        dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls
                        name={sym.charAt(0).toUpperCase() + sym.slice(1)} />
                    ))}
                    {showRecommLine && (
                      <Line type="monotone" dataKey="recommendationScore"
                        stroke={SYMPTOM_COLORS.recommendationScore} strokeWidth={2}
                        strokeDasharray="6 3" dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls
                        name="Recomm. Score" />
                    )}
                    {showImproveLine && (
                      <Line type="monotone" dataKey="overallImprovement"
                        stroke={SYMPTOM_COLORS.overallImprovement} strokeWidth={2}
                        strokeDasharray="3 2" dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls
                        name="Overall Impr." />
                    )}
                  </LineChart>
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                    Solid = symptoms (0–10) · Dashed green = Recomm. score · Dashed cyan = Overall improvement
                  </div>
                </div>
              ) : (
                /* TABLE VIEW for pain trend */
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 18px', boxShadow: '0 2px 8px rgba(0,0,0,.07)', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr>
                        <TH>Session</TH>
                        <TH>Date</TH>
                        {shownSymptoms.map(sym => (
                          <TH key={sym} color={SYMPTOM_COLORS[sym]}>{sym.charAt(0).toUpperCase() + sym.slice(1)}</TH>
                        ))}
                        {showRecommLine && <TH color="#16a34a">Recomm. Score</TH>}
                        {showImproveLine && <TH color="#0891b2">Overall Impr.</TH>}
                      </tr>
                    </thead>
                    <tbody>
                      {painTrendData.map((r, i) => (
                        <tr key={i} style={{ background: i % 2 ? '#fafbfd' : '#fff' }}>
                          <TD><strong>{r.label}</strong></TD>
                          <TD>{r.fullDate}</TD>
                          {shownSymptoms.map(sym => (
                            <TD key={sym}>
                              {r[sym] != null
                                ? <span style={{ fontWeight: 700, color: SYMPTOM_COLORS[sym] }}>{r[sym]}/10</span>
                                : <span style={{ color: '#d1d5db' }}>—</span>}
                            </TD>
                          ))}
                          {showRecommLine && (
                            <TD>
                              {r.recommendationScore != null
                                ? <span style={{ fontWeight: 700, color: '#16a34a' }}>{r.recommendationScore}/10</span>
                                : <span style={{ color: '#d1d5db' }}>—</span>}
                            </TD>
                          )}
                          {showImproveLine && (
                            <TD>
                              {r.overallImprovement != null
                                ? <span style={{ fontWeight: 700, color: '#0891b2' }}>{r.overallImprovement}/10</span>
                                : <span style={{ color: '#d1d5db' }}>—</span>}
                            </TD>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ══ ROM & MUSCLE PROGRESS ══ */}
          {tab === 'progress' && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1d23', marginBottom: 4 }}>
                📈 ROM & Muscle Strength — All Assessed Body Parts
              </div>
              <div style={{ fontSize: 11, color: '#888', marginBottom: 16 }}>
                Solid lines = ROM (°) · Dashed lines = Muscle (MVC 0–5) · Toggle each card to table view for exact numbers
              </div>

              {histLoading ? (
                <div style={{ color: '#aaa', textAlign: 'center', padding: '40px 0' }}>Loading assessment history…</div>
              ) : romHistory.length === 0 ? (
                <div style={{ color: '#aaa', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>
                  No ROM assessments saved yet. Use the ROM form below to record measurements.
                </div>
              ) : allRomParts.size === 0 ? (
                <div style={{ color: '#aaa', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>
                  No body part data found in ROM history.
                </div>
              ) : (
                [...allRomParts.entries()].map(([romKey, info]) => (
                  <PartProgressChart
                    key={romKey} romPartKey={romKey}
                    label={info.label + (info.isConsult ? '' : ' (other)')}
                    history={romHistory} mode="all"
                  />
                ))
              )}

              {/* Full ROM entry panel (collapsible) */}
              {!histLoading && (
                <div style={{ marginTop: 12 }}>
                  <button
                    onClick={() => setShowRomPanel(p => !p)}
                    style={{
                      background: showRomPanel ? '#f1f5f9' : '#1565c0', color: showRomPanel ? '#374151' : '#fff',
                      border: showRomPanel ? '1px solid #d1d5db' : 'none',
                      borderRadius: 8, padding: '8px 18px', fontSize: 12,
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {showRomPanel ? '▲ Hide ROM Assessment Form' : '▼ Open ROM Assessment Form'}
                  </button>
                  {showRomPanel && (
                    <div style={{ marginTop: 14 }}>
                      <RomAssessmentPanel patientCard={patientCard} patientName={patientName} />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ══ RECOMMENDATIONS LIST ══ */}
          {tab === 'recomm' && (
            <div>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1d23', marginBottom: 4 }}>📋 Recommendations — Compliance & History</div>

              {/* ── Recommendation Compliance Trend Chart ── */}
              {recommTrendData.length >= 2 && (
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 12px', boxShadow: '0 2px 8px rgba(0,0,0,.07)', marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#1e3a5f', marginBottom: 10 }}>
                    📊 Recommendation Compliance Trend
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <LineChart
                      width={Math.min(840, recommTrendData.length * 68 + 100)} height={240}
                      data={recommTrendData}
                      margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11 }} />
                      <Tooltip content={<PainTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Line type="monotone" dataKey="recommendationScore"
                        stroke="#16a34a" strokeWidth={2.5} dot={{ r: 4, fill: '#16a34a' }}
                        activeDot={{ r: 6 }} connectNulls name="Recomm. Score" />
                      <Line type="monotone" dataKey="overallImprovement"
                        stroke="#0891b2" strokeWidth={2.5} strokeDasharray="6 3"
                        dot={{ r: 4, fill: '#0891b2' }} activeDot={{ r: 6 }} connectNulls
                        name="Overall Improvement" />
                    </LineChart>
                  </div>
                  <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                    Higher score = better compliance & improvement (0–10 scale)
                  </div>
                </div>
              )}

              {/* ── Per-session compliance bar chart ── */}
              {recommTrendData.length >= 2 && (
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 12px', boxShadow: '0 2px 8px rgba(0,0,0,.07)', marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, color: '#1e3a5f', marginBottom: 10 }}>
                    📊 Session-wise Compliance Score
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <BarChart
                      width={Math.min(840, recommTrendData.length * 68 + 100)} height={200}
                      data={recommTrendData}
                      margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fontSize: 11 }} />
                      <Tooltip content={<PainTooltip />} />
                      <Bar dataKey="recommendationScore" name="Recomm. Score" radius={[4, 4, 0, 0]}>
                        {recommTrendData.map((entry, idx) => {
                          const val = entry.recommendationScore || 0;
                          const color = val >= 7 ? '#16a34a' : val >= 4 ? '#f59e0b' : '#ef4444';
                          return <Cell key={idx} fill={color} />;
                        })}
                      </Bar>
                    </BarChart>
                  </div>
                  <div style={{ display: 'flex', gap: 14, fontSize: 10, color: '#9ca3af', marginTop: 4 }}>
                    <span><span style={{ color: '#16a34a' }}>●</span> Good (7–10)</span>
                    <span><span style={{ color: '#f59e0b' }}>●</span> Moderate (4–6)</span>
                    <span><span style={{ color: '#ef4444' }}>●</span> Low (0–3)</span>
                  </div>
                </div>
              )}

              {recommTrendData.length < 2 && recommTrendData.length > 0 && (
                <div style={{ background: '#fffbeb', borderRadius: 10, padding: '12px 16px', border: '1px solid #fde68a', marginBottom: 16, fontSize: 12, color: '#92400e' }}>
                  ⚠ Only {recommTrendData.length} session with recommendation scores. Need at least 2 to show trend charts.
                </div>
              )}

              {/* Current assigned */}
              {selectedRecomms?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    ✅ Currently Assigned
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {selectedRecomms.map((r, i) => (
                      <div key={r._id || i} style={{ background: '#f0fdf4', borderRadius: 8, padding: '10px 14px', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 16 }}>✅</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#166534' }}>{r.recommendation}</div>
                          <div style={{ fontSize: 11, color: '#4d7c0f', marginTop: 2 }}>
                            {r.qty ? `${r.qty} × ` : ''}{r.not ? `${r.not}/day` : ''}{r.nod ? ` · ${r.nod} days` : ''}{r.rtype ? ` · ${r.rtype}` : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* History per plan submission */}
              {reviewHistory.filter(rv => rv.recommendations?.length > 0).length > 0 ? (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                    📅 History by Consultation
                  </div>
                  {reviewHistory.filter(rv => rv.recommendations?.length > 0).map((rv, ri) => (
                    <div key={ri} style={{ marginBottom: 12, background: '#f9fafb', borderRadius: 10, padding: '10px 14px', border: '1px solid #e5e7eb' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                        {rv.date ? new Date(rv.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        {rv.reviewedBy && <span style={{ fontWeight: 400, color: '#9ca3af' }}> · {rv.reviewedBy}</span>}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {rv.recommendations.map((r, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12, color: '#374151' }}>
                            <span style={{ color: '#9ca3af', flexShrink: 0 }}>•</span>
                            <span>{r.text || r.recommendation}</span>
                            {r.qty  && <span style={{ fontSize: 10, background: '#e5e7eb', borderRadius: 4, padding: '1px 5px', color: '#555' }}>{r.qty}×</span>}
                            {r.not  && <span style={{ fontSize: 10, background: '#e5e7eb', borderRadius: 4, padding: '1px 5px', color: '#555' }}>{r.not}/day</span>}
                            {r.nod  && <span style={{ fontSize: 10, background: '#e5e7eb', borderRadius: 4, padding: '1px 5px', color: '#555' }}>{r.nod}d</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : !selectedRecomms?.length ? (
                <div style={{ color: '#aaa', textAlign: 'center', padding: '40px 0', fontSize: 13 }}>No recommendations recorded yet.</div>
              ) : null}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
